import React, { useState, useEffect, useRef } from 'react';
import { Save, AlertCircle, CheckCircle } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { sanitizeHtml } from '../../utils/sanitize';

const PrivacyPolicyManager = () => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const previewRef = useRef(null);

  // Remove inline styles from preview in dark mode
  useEffect(() => {
    if (previewRef.current) {
      const allElements = previewRef.current.querySelectorAll('*');
      allElements.forEach(el => {
        el.removeAttribute('style');
      });
    }
  }, [content]);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/privacy-policy`);
      if (response.ok) {
        const data = await response.json();
        setContent(data.content);
        setLastUpdated(data.updated_at);
        setUpdatedBy(data.updated_by);
      }
    } catch (error) {
      console.error('Error fetching privacy policy:', error);
      setMessage({ type: 'error', text: 'Failed to load privacy policy' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/privacy-policy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content })
      });

      if (response.ok) {
        const data = await response.json();
        setLastUpdated(data.updated_at);
        setUpdatedBy(data.updated_by);
        setMessage({ type: 'success', text: 'Privacy policy updated successfully!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        throw new Error('Failed to update privacy policy');
      }
    } catch (error) {
      console.error('Error updating privacy policy:', error);
      setMessage({ type: 'error', text: 'Failed to update privacy policy' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Privacy Policy Manager</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Edit and manage the privacy policy content</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {message.text && (
        <div className={`flex items-center gap-3 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {lastUpdated && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <strong>Last updated:</strong> {new Date(lastUpdated).toLocaleString()}
            {updatedBy && ` by ${updatedBy}`}
          </p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
          Privacy Policy Content
        </label>
        <div className="jodit-dark-wrapper">
          <RichTextEditor
            value={content}
            onChange={setContent}
            placeholder="Enter privacy policy content here..."
          />
        </div>
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          Use the toolbar above to format text (bold, italic, headings, lists, etc.)
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Preview</h3>
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-6 max-h-96 overflow-y-auto bg-gray-50 dark:bg-gray-700/50 privacy-preview-container">
          <div 
            ref={previewRef}
            className="prose max-w-none dark:prose-invert prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-strong:text-gray-900 dark:prose-strong:text-white prose-headings:text-gray-900 dark:prose-headings:text-white"
            dangerouslySetInnerHTML={{ 
              __html: sanitizeHtml(content) || '<p class="text-gray-500 dark:text-gray-400">No content to preview</p>'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyManager;
